import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
import { ArrowLeft, User, MapPin, Shield, Clock, Activity, Briefcase, FileText, Map, Navigation, List, CheckCircle, XCircle, AlertTriangle, BarChart2, Smartphone, Battery, Wifi, WifiOff, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { getPersonnelOps, statusMarkerColor, isOnline, totalDistanceKm, type CheckInEntry } from '../data/personnelOps';
import { PersonnelMap } from '../components/PersonnelMap';
export default function PersonnelDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profil');
  const person = id ? getPersonnelOps(id) : undefined;
  const [checkIns, setCheckIns] = useState<CheckInEntry[]>(person ? person.checkIns : []);
  const [attachPhoto, setAttachPhoto] = useState(false);
  if (!person) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4" data-id="element-2753">
        <h2 className="text-xl font-mono text-muted-foreground" data-id="element-2754">
          Personel tidak ditemukan
        </h2>
        <Button onClick={() => navigate('/personnel')} variant="outline" data-id="element-2755">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2756" /> Kembali ke Daftar
        </Button>
      </div>;
  }
  const online = isOnline(person.status);
  const statusColor = statusMarkerColor(person.status);
  const totalDistance = totalDistanceKm(person.movementHistory);
  const lastCheckIsIn = checkIns.length > 0 ? checkIns[0].kind === 'Check In' : false;
  const handleCheck = (kind: 'Check In' | 'Check Out') => {
    const jitter = () => (Math.random() - 0.5) * 0.02;
    const lat = +(person.currentPosition.lat + jitter()).toFixed(5);
    const lng = +(person.currentPosition.lng + jitter()).toFixed(5);
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `24 Jun 2026 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const entry: CheckInEntry = {
      id: `${person.id}-CHK-${Date.now()}`,
      kind,
      timestamp: ts,
      lat,
      lng,
      location: person.currentPosition.kecamatan,
      hasPhoto: attachPhoto
    };
    setCheckIns(prev => [entry, ...prev]);
    setAttachPhoto(false);
    toast.success(`${kind} berhasil — GPS ${lat}, ${lng} tercatat pukul ${pad(d.getHours())}:${pad(d.getMinutes())}.`);
  };
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto pb-10" data-id="element-2757">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-2758">
        <Button variant="ghost" size="icon" onClick={() => navigate('/personnel')} className="hover:bg-primary/20 hover:text-primary" data-id="element-2759">
          <ArrowLeft className="w-5 h-5" data-id="element-2760" />
        </Button>
        <div data-id="element-2761">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-2762">
            PROFIL PERSONEL
            <span className="text-sm font-mono text-primary bg-primary/10 px-2 py-1 rounded border border-primary/30" data-id="element-2763">
              {person.id}
            </span>
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-2764">
            Data Operatif & Penugasan
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2765">
        {/* Left Column: Profile Card */}
        <Card className="glass-panel-heavy shadow-[0_0_30px_rgba(0,0,0,0.5)] md:col-span-1 h-fit" style={{
        borderColor: `color-mix(in srgb, ${statusColor} 30%, transparent)`
      }} data-id="element-2766">
          <CardHeader className="p-6 border-b border-border/50 bg-black/20 flex flex-col items-center text-center relative overflow-hidden" data-id="element-2767">
            <div className="absolute top-0 left-0 w-full h-1" style={{
            backgroundColor: statusColor
          }} data-id="element-2768" />

            <div className="relative mb-4" data-id="element-2769">
              <img src={person.photo} alt={person.name} className="w-32 h-32 rounded-full object-cover border-4" style={{
              borderColor: statusColor
            }} data-id="element-2770" />
              <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full border-2 border-background flex items-center justify-center" style={{
              backgroundColor: statusColor
            }} data-id="element-2771">
                {online ? <Wifi className="w-3 h-3 text-black" data-id="element-2772" /> : <WifiOff className="w-3 h-3 text-black" data-id="element-2773" />}
              </div>
            </div>

            <CardTitle className="text-2xl font-heading leading-tight" data-id="element-2774">
              {person.name}
            </CardTitle>
            <p className="text-sm font-mono text-muted-foreground mt-2" data-id="element-2775">
              {person.jabatan}
            </p>
            <Badge variant="outline" className="mt-4 text-xs font-mono px-3 py-1" style={{
            color: statusColor,
            borderColor: statusColor,
            backgroundColor: `color-mix(in srgb, ${statusColor} 10%, transparent)`
          }} data-id="element-2776">
              {person.status}
            </Badge>
          </CardHeader>
          <CardContent className="p-6 space-y-4" data-id="element-2777">
            <div className="flex justify-between items-center bg-black/20 p-3 rounded border border-border/30" data-id="element-2778">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2" data-id="element-2779">
                <Shield className="w-4 h-4" data-id="element-2780" /> Unit
              </span>
              <span className="font-mono text-xs text-right max-w-[140px]" data-id="element-2781">
                {person.unit}
              </span>
            </div>
            <div className="flex justify-between items-center bg-black/20 p-3 rounded border border-border/30" data-id="element-2782">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2" data-id="element-2783">
                <MapPin className="w-4 h-4" data-id="element-2784" /> Wilayah
              </span>
              <span className="font-mono text-xs" data-id="element-2785">{person.provinsi}</span>
            </div>
            <div className="flex justify-between items-center bg-black/20 p-3 rounded border border-border/30" data-id="element-2786">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2" data-id="element-2787">
                <Clock className="w-4 h-4" data-id="element-2788" /> Aktivitas
              </span>
              <span className="font-mono text-xs" data-id="element-2789">{person.lastActivity}</span>
            </div>

            <div className="pt-4 border-t border-border/50" data-id="element-2790">
              <h4 className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3" data-id="element-2791">
                Status Perangkat
              </h4>
              <div className="grid grid-cols-2 gap-2" data-id="element-2792">
                <div className="bg-black/20 p-2 rounded border border-border/30 flex flex-col items-center justify-center text-center gap-1" data-id="element-2793">
                  <Smartphone className="w-4 h-4 text-primary" data-id="element-2794" />
                  <span className="text-[10px] font-mono text-muted-foreground" data-id="element-2795">
                    {person.device}
                  </span>
                </div>
                <div className="bg-black/20 p-2 rounded border border-border/30 flex flex-col items-center justify-center text-center gap-1" data-id="element-2796">
                  {person.deviceStatus === 'Baterai Lemah' ? <Battery className="w-4 h-4 text-destructive" data-id="element-2797" /> : <Battery className="w-4 h-4 text-[var(--status-active)]" data-id="element-2798" />}
                  <span className="text-[10px] font-mono text-muted-foreground" data-id="element-2799">
                    {person.deviceStatus}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Column: Tabs */}
        <div className="md:col-span-2" data-id="element-2800">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-2801">
            <TabsList className="grid w-full grid-cols-5 mb-6 bg-black/40 p-1 rounded-md border border-border/50" data-id="element-2802">
              <TabsTrigger value="profil" className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-1" data-id="element-2803">
                Profil
              </TabsTrigger>
              <TabsTrigger value="posisi" className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-1" data-id="element-2804">
                Posisi
              </TabsTrigger>
              <TabsTrigger value="aktivitas" className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-1" data-id="element-2805">
                Aktivitas
              </TabsTrigger>
              <TabsTrigger value="laporan" className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-1" data-id="element-2806">
                Laporan
              </TabsTrigger>
              <TabsTrigger value="kpi" className="text-[10px] sm:text-xs font-mono uppercase tracking-widest px-1" data-id="element-2807">
                KPI
              </TabsTrigger>
            </TabsList>

            {/* TAB: PROFIL & STATUS */}
            <TabsContent value="profil" className="space-y-6 mt-0" data-id="element-2808">
              <Card className="glass-panel border-border/50" data-id="element-2809">
                <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2810">
                  <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2811">
                    <User className="w-4 h-4 text-primary" data-id="element-2812" /> Informasi
                    Identitas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2813">
                  <div className="space-y-1" data-id="element-2814">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2815">
                      Nama Lengkap
                    </p>
                    <p className="font-medium text-sm" data-id="element-2816">{person.name}</p>
                  </div>
                  <div className="space-y-1" data-id="element-2817">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2818">
                      ID Personel
                    </p>
                    <p className="font-mono text-sm text-primary" data-id="element-2819">
                      {person.id}
                    </p>
                  </div>
                  <div className="space-y-1" data-id="element-2820">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2821">
                      BINDA
                    </p>
                    <p className="font-medium text-sm" data-id="element-2822">{person.binda}</p>
                  </div>
                  <div className="space-y-1" data-id="element-2823">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2824">
                      Tingkat Izin
                    </p>
                    <p className="font-mono text-sm" data-id="element-2825">{person.clearance}</p>
                  </div>
                  <div className="space-y-1" data-id="element-2826">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2827">
                      Peran Sistem
                    </p>
                    <p className="font-medium text-sm" data-id="element-2828">{person.role}</p>
                  </div>
                  <div className="space-y-1" data-id="element-2829">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2830">
                      Sinkronisasi Terakhir
                    </p>
                    <p className="font-mono text-sm" data-id="element-2831">{person.lastSync}</p>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-4" data-id="element-2832">
                <Card className="glass-panel border-border/50" data-id="element-2833">
                  <CardContent className="p-4 flex items-center gap-4" data-id="element-2834">
                    <div className="p-3 bg-primary/10 rounded border border-primary/30" data-id="element-2835">
                      <FileText className="w-5 h-5 text-primary" data-id="element-2836" />
                    </div>
                    <div data-id="element-2837">
                      <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2838">
                        Total Laporan
                      </p>
                      <p className="text-xl font-mono font-bold text-foreground text-glow-primary" data-id="element-2839">
                        {person.reportsCreated}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-panel border-border/50" data-id="element-2840">
                  <CardContent className="p-4 flex items-center gap-4" data-id="element-2841">
                    <div className="p-3 bg-[var(--status-active)]/10 rounded border border-[var(--status-active)]/30" data-id="element-2842">
                      <CheckCircle className="w-5 h-5 text-[var(--status-active)]" data-id="element-2843" />
                    </div>
                    <div data-id="element-2844">
                      <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest" data-id="element-2845">
                        Laporan Diterima
                      </p>
                      <p className="text-xl font-mono font-bold text-[var(--status-active)] text-glow-active" data-id="element-2846">
                        {person.reportsAccepted}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* TAB: POSISI & PERGERAKAN */}
            <TabsContent value="posisi" className="space-y-6 mt-0" data-id="element-2847">
              <Card className="glass-panel border-border/50" data-id="element-2848">
                <CardHeader className="border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-2849">
                  <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2850">
                    <Navigation className="w-4 h-4 text-primary" data-id="element-2851" /> Posisi
                    Terkini
                  </CardTitle>
                  {!online && <Badge variant="outline" className="text-[10px] font-mono border-destructive text-destructive" data-id="element-2852">
                      LAST KNOWN POSITION
                    </Badge>}
                </CardHeader>
                <CardContent className="p-0" data-id="element-2853">
                  <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-border/50" data-id="element-2854">
                    <div className="p-4 border-r border-border/50 space-y-1" data-id="element-2855">
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2856">
                        Koordinat
                      </p>
                      <p className="font-mono text-xs" data-id="element-2857">
                        {person.currentPosition.lat},{' '}
                        {person.currentPosition.lng}
                      </p>
                    </div>
                    <div className="p-4 border-r border-border/50 space-y-1" data-id="element-2858">
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2859">
                        Akurasi
                      </p>
                      <p className="font-mono text-xs" data-id="element-2860">
                        ± {person.currentPosition.accuracy} meter
                      </p>
                    </div>
                    <div className="p-4 space-y-1" data-id="element-2861">
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2862">
                        Waktu
                      </p>
                      <p className="font-mono text-xs" data-id="element-2863">
                        {person.currentPosition.timestamp}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-black/20" data-id="element-2864">
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-1" data-id="element-2865">
                      Alamat
                    </p>
                    <p className="text-sm" data-id="element-2866">
                      {person.currentPosition.address}, Kec.{' '}
                      {person.currentPosition.kecamatan},{' '}
                      {person.currentPosition.kabupaten},{' '}
                      {person.currentPosition.provinsi}
                    </p>
                  </div>
                  <div className="h-[300px] w-full" data-id="element-2867">
                    <PersonnelMap personnel={[person]} height="100%" showPathFor={person.id} center={[person.currentPosition.lat, person.currentPosition.lng]} zoom={14} data-id="element-2868" />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-panel border-border/50" data-id="element-2869">
                <CardHeader className="border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-2870">
                  <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2871">
                    <MapPin className="w-4 h-4 text-primary" data-id="element-2872" /> Riwayat
                    Pergerakan
                  </CardTitle>
                  <div className="text-[10px] font-mono text-muted-foreground" data-id="element-2873">
                    Total Jarak:{' '}
                    <span className="text-primary font-bold" data-id="element-2874">
                      {totalDistance} km
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-6" data-id="element-2875">
                  <div className="relative space-y-6 before:absolute before:inset-0 before:ml-[11px] before:h-full before:w-0.5 before:bg-border/50" data-id="element-2876">
                    {person.movementHistory.map((m, idx) => <div key={idx} className="relative flex gap-4" data-id="element-2877">
                        <div className="w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center shrink-0 z-10" data-id="element-2878">
                          <div className="w-2 h-2 rounded-full bg-primary" data-id="element-2879" />
                        </div>
                        <div className="flex-1 bg-black/20 p-3 rounded border border-border/30" data-id="element-2880">
                          <div className="flex justify-between items-start mb-1" data-id="element-2881">
                            <h4 className="font-bold text-sm" data-id="element-2882">{m.label}</h4>
                            <span className="text-[10px] font-mono text-primary" data-id="element-2883">
                              {m.time}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2" data-id="element-2884">
                            {m.note}
                          </p>
                          <p className="text-[10px] font-mono text-muted-foreground/70" data-id="element-2885">
                            {m.lat}, {m.lng}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: AKTIVITAS */}
            <TabsContent value="aktivitas" className="space-y-6 mt-0" data-id="element-2886">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2887">
                <Card className="glass-panel border-border/50" data-id="element-2888">
                  <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2889">
                    <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2890">
                      <List className="w-4 h-4 text-primary" data-id="element-2891" /> Log Aktivitas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0" data-id="element-2892">
                    <div className="divide-y divide-border/50" data-id="element-2893">
                      {person.activityLog.map(act => <div key={act.id} className="p-4 hover:bg-white/5 transition-colors" data-id="element-2894">
                          <div className="flex justify-between items-start mb-2" data-id="element-2895">
                            <div className="flex items-center gap-2" data-id="element-2896">
                              {act.result === 'Berhasil' ? <CheckCircle className="w-3 h-3 text-[var(--status-active)]" data-id="element-2897" /> : act.result === 'Gagal' ? <XCircle className="w-3 h-3 text-destructive" data-id="element-2898" /> : <AlertTriangle className="w-3 h-3 text-[var(--status-standby)]" data-id="element-2899" />}
                              <span className="font-bold text-xs" data-id="element-2900">
                                {act.type}
                              </span>
                            </div>
                            <span className="text-[10px] font-mono text-muted-foreground" data-id="element-2901">
                              {act.date} • {act.time}
                            </span>
                          </div>
                          <div className="text-[10px] font-mono text-muted-foreground flex justify-between" data-id="element-2902">
                            <span data-id="element-2903">{act.location}</span>
                            <span data-id="element-2904">{act.device}</span>
                          </div>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-6" data-id="element-2905">
                  <Card className="glass-panel border-border/50" data-id="element-2906">
                    <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2907">
                      <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2908">
                        <MapPin className="w-4 h-4 text-primary" data-id="element-2909" /> Sistem
                        Check-In
                      </CardTitle>
                    </CardHeader>
                    <div className="p-4 border-b border-border/50 bg-black/20 space-y-3" data-id="element-2910">
                      <label className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground uppercase tracking-wider cursor-pointer" data-id="element-2911">
                        <input type="checkbox" checked={attachPhoto} onChange={e => setAttachPhoto(e.target.checked)} className="accent-primary" data-id="element-2912" />
                        Lampirkan Foto (opsional)
                      </label>
                      <div className="grid grid-cols-2 gap-2" data-id="element-2913">
                        <Button size="sm" disabled={lastCheckIsIn} onClick={() => handleCheck('Check In')} className="h-8 text-[10px] font-mono uppercase tracking-widest bg-[var(--status-active)]/20 text-[var(--status-active)] border border-[var(--status-active)]/50 hover:bg-[var(--status-active)] hover:text-black disabled:opacity-40" data-id="element-2914">
                          <CheckCircle className="w-3.5 h-3.5 mr-1.5" data-id="element-2915" /> Check
                          In
                        </Button>
                        <Button size="sm" disabled={!lastCheckIsIn && checkIns.length > 0} onClick={() => handleCheck('Check Out')} variant="outline" className="h-8 text-[10px] font-mono uppercase tracking-widest border-border/50 disabled:opacity-40" data-id="element-2916">
                          <Navigation className="w-3.5 h-3.5 mr-1.5" data-id="element-2917" /> Check
                          Out
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-0" data-id="element-2918">
                      <div className="divide-y divide-border/50" data-id="element-2919">
                        {checkIns.map(chk => <div key={chk.id} className="p-4" data-id="element-2920">
                            <div className="flex justify-between items-center mb-2" data-id="element-2921">
                              <Badge variant="outline" className={`text-[10px] font-mono ${chk.kind === 'Check In' ? 'border-[var(--status-active)] text-[var(--status-active)]' : 'border-muted-foreground text-muted-foreground'}`} data-id="element-2922">
                                {chk.kind}
                              </Badge>
                              <span className="text-[10px] font-mono text-muted-foreground" data-id="element-2923">
                                {chk.timestamp}
                              </span>
                            </div>
                            <p className="text-xs font-bold mb-1" data-id="element-2924">
                              {chk.location}
                            </p>
                            <div className="flex justify-between items-center" data-id="element-2925">
                              <p className="text-[10px] font-mono text-muted-foreground" data-id="element-2926">
                                {chk.lat}, {chk.lng}
                              </p>
                              {chk.hasPhoto && <span className="text-[10px] font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded" data-id="element-2927">
                                  Foto Terlampir
                                </span>}
                            </div>
                          </div>)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass-panel border-border/50" data-id="element-2928">
                    <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2929">
                      <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2930">
                        <Shield className="w-4 h-4 text-primary" data-id="element-2931" /> Mode Patroli
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4" data-id="element-2932">
                      <div className="flex justify-between items-center mb-4" data-id="element-2933">
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2934">
                          Status Patroli
                        </span>
                        <Badge variant="outline" className="text-[10px] font-mono border-primary text-primary bg-primary/10" data-id="element-2935">
                          {person.patrol.status}
                        </Badge>
                      </div>
                      <div className="space-y-2" data-id="element-2936">
                        <div className="flex justify-between text-xs" data-id="element-2937">
                          <span className="text-muted-foreground" data-id="element-2938">
                            Titik Selesai
                          </span>
                          <span className="font-mono font-bold" data-id="element-2939">
                            {person.patrol.completedRoute.length}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs" data-id="element-2940">
                          <span className="text-muted-foreground" data-id="element-2941">
                            Titik Tersisa
                          </span>
                          <span className="font-mono font-bold" data-id="element-2942">
                            {person.patrol.currentRoute.length}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* TAB: LAPORAN */}
            <TabsContent value="laporan" className="space-y-6 mt-0" data-id="element-2943">
              <Card className="glass-panel border-border/50" data-id="element-2944">
                <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2945">
                  <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2946">
                    <FileText className="w-4 h-4 text-primary" data-id="element-2947" /> Relasi Laporan
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0" data-id="element-2948">
                  <div className="divide-y divide-border/50" data-id="element-2949">
                    {person.reports.map(rep => <div key={rep.id} className="p-4 hover:bg-white/5 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4" onClick={() => navigate(`/reports/${rep.id}`)} data-id="element-2950">
                        <div data-id="element-2951">
                          <div className="flex items-center gap-2 mb-1" data-id="element-2952">
                            <span className="text-xs font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded border border-primary/30" data-id="element-2953">
                              {rep.id}
                            </span>
                            <span className="text-[10px] font-mono text-muted-foreground" data-id="element-2954">
                              {rep.time}
                            </span>
                          </div>
                          <p className="font-bold text-sm" data-id="element-2955">{rep.title}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] font-mono shrink-0 ${rep.state === 'Diterima' ? 'border-[var(--status-active)] text-[var(--status-active)]' : rep.state === 'Ditolak' ? 'border-destructive text-destructive' : rep.state === 'Diverifikasi' ? 'border-[var(--status-info)] text-[var(--status-info)]' : 'border-muted-foreground text-muted-foreground'}`} data-id="element-2956">
                          {rep.state}
                        </Badge>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: KPI */}
            <TabsContent value="kpi" className="space-y-6 mt-0" data-id="element-2957">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2958">
                <Card className="glass-panel border-border/50" data-id="element-2959">
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full" data-id="element-2960">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2" data-id="element-2961">
                      Skor KPI Keseluruhan
                    </p>
                    <div className="text-5xl font-mono font-bold text-primary text-glow-primary mb-2" data-id="element-2962">
                      {person.kpi.score}
                    </div>
                    <div className={`flex items-center gap-1 text-xs font-mono ${person.kpi.trend >= 0 ? 'text-[var(--status-active)]' : 'text-destructive'}`} data-id="element-2963">
                      {person.kpi.trend >= 0 ? <TrendingUp className="w-3 h-3" data-id="element-2964" /> : <TrendingDown className="w-3 h-3" data-id="element-2965" />}
                      {Math.abs(person.kpi.trend)}% dari periode lalu
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-4" data-id="element-2966">
                  <Card className="glass-panel border-border/50" data-id="element-2967">
                    <CardContent className="p-4 flex justify-between items-center" data-id="element-2968">
                      <div data-id="element-2969">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2970">
                          Peringkat BINDA
                        </p>
                        <p className="text-lg font-mono font-bold text-foreground" data-id="element-2971">
                          #{person.kpi.rankingBinda}{' '}
                          <span className="text-xs text-muted-foreground" data-id="element-2972">
                            / {person.kpi.rankingBindaTotal}
                          </span>
                        </p>
                      </div>
                      <BarChart2 className="w-6 h-6 text-primary opacity-50" data-id="element-2973" />
                    </CardContent>
                  </Card>
                  <Card className="glass-panel border-border/50" data-id="element-2974">
                    <CardContent className="p-4 flex justify-between items-center" data-id="element-2975">
                      <div data-id="element-2976">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-2977">
                          Peringkat Nasional
                        </p>
                        <p className="text-lg font-mono font-bold text-foreground" data-id="element-2978">
                          #{person.kpi.rankingNasional}{' '}
                          <span className="text-xs text-muted-foreground" data-id="element-2979">
                            / {person.kpi.rankingNasionalTotal}
                          </span>
                        </p>
                      </div>
                      <BarChart2 className="w-6 h-6 text-[var(--status-info)] opacity-50" data-id="element-2980" />
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Card className="glass-panel border-border/50" data-id="element-2981">
                <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-2982">
                  <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-2983">
                    <Activity className="w-4 h-4 text-primary" data-id="element-2984" /> Metrik Kinerja
                    Detail
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6" data-id="element-2985">
                  <div className="space-y-2" data-id="element-2986">
                    <div className="flex justify-between text-xs font-mono" data-id="element-2987">
                      <span className="text-muted-foreground" data-id="element-2988">
                        Produktivitas Laporan
                      </span>
                      <span className="font-bold" data-id="element-2989">
                        {person.kpi.productivity}%
                      </span>
                    </div>
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden" data-id="element-2990">
                      <div className="h-full bg-primary" style={{
                      width: `${person.kpi.productivity}%`
                    }} data-id="element-2991" />
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2992">
                    <div className="flex justify-between text-xs font-mono" data-id="element-2993">
                      <span className="text-muted-foreground" data-id="element-2994">
                        Kualitas Data (Akurasi)
                      </span>
                      <span className="font-bold" data-id="element-2995">{person.kpi.quality}%</span>
                    </div>
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden" data-id="element-2996">
                      <div className="h-full bg-[var(--status-info)]" style={{
                      width: `${person.kpi.quality}%`
                    }} data-id="element-2997" />
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2998">
                    <div className="flex justify-between text-xs font-mono" data-id="element-2999">
                      <span className="text-muted-foreground" data-id="element-3000">
                        Ketepatan Waktu (SLA)
                      </span>
                      <span className="font-bold" data-id="element-3001">
                        {person.kpi.timeliness}%
                      </span>
                    </div>
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden" data-id="element-3002">
                      <div className="h-full bg-[var(--status-standby)]" style={{
                      width: `${person.kpi.timeliness}%`
                    }} data-id="element-3003" />
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-3004">
                    <div className="flex justify-between text-xs font-mono" data-id="element-3005">
                      <span className="text-muted-foreground" data-id="element-3006">
                        Tingkat Penerimaan Laporan
                      </span>
                      <span className="font-bold" data-id="element-3007">
                        {person.kpi.acceptanceRate}%
                      </span>
                    </div>
                    <div className="h-2 bg-black/40 rounded-full overflow-hidden" data-id="element-3008">
                      <div className="h-full bg-[var(--status-active)]" style={{
                      width: `${person.kpi.acceptanceRate}%`
                    }} data-id="element-3009" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>;
}